import { useRef } from "react";
import { fileReadUtil } from "../../import_export/utility/ImportFile";
import { acceptedFileFormats, supportedFileTypes } from "../../import_export/EximConstants";
import {
    DKLabel,
    DKButton,
    showToast,
    showAlert,
    TOAST_TYPE,
    showLoader,
    removeLoader,
    DKIcons,
    DKIcon,
} from "deskera-ui-library";
import UploadCloud from "../../../assets/images/upload_cloud_outlined.svg";
import UploadCloudFilled from "../../../assets/images/upload_cloud_filled.svg";
import { onDownload } from "../../import_export/utility/ExportData";
import { CONSTANTS } from "../../../constants/Constant";
import { enrichMapping } from "../../import_export/utility/ImportUtility";
import { TENANT, TenantService } from "../../../managers/TenantManagerService";
import { TABLES } from "../../../managers/TableManger";
import { selectCustomField } from "../../../Redux/Slices/CustomeFieldSlice";
import { useSelector } from "react-redux";
import ComponentImportManager from "../../Component/import/ComponentImportManager";
import PermissionUtils, { ACTION, MODULE } from "../../user-permissions/PermissionUtils";
import i18next from "i18next";

const EXCLUDE_FIELDS = ["componentGroup", "employerBearingPcb"];

const UploadFile = (props) => {
    const uploadInput = useRef();
    const cloudImageRef = useRef();
    const customFieldResponse = useSelector(selectCustomField);

    const isFileAcceptable = (selectedFile) => {
        if (!selectedFile) return false;
        return acceptedFileFormats.some((acceptedFormat) =>
            selectedFile.name.includes(acceptedFormat)
        );
    };

    const handleFiles = (selectedFiles) => {
        if (!selectedFiles.length) {
            showToast("File not found", TOAST_TYPE.FAILURE);
        }

        const selectedFile = selectedFiles[0];
        if (isFileAcceptable(selectedFile)) {
            showLoader("Processing the uploaded file...");

            /* Parsing the file at Client End */
            fileReadUtil(selectedFile, supportedFileTypes.CSV)
                .then((res) => {
                    props.setData({
                        fileInfo: {
                            name: selectedFile.name,
                            size: selectedFile.size,
                            mimeType: selectedFile.type,
                        },
                        fileContent: res,
                        /* Reset mapped values whenever new file is uploaded */
                        mappedHeaders: null,
                        importRecords: null,
                        invalidRows: null,
                    });
                })
                .catch((err) => {
                    showToast(err.message, TOAST_TYPE.FAILURE);
                });

            /* Uploading the file for Import Logs */
            props.generateAuditLog(selectedFile).then(
                (response) => {
                    const { filePath, columnMapping } = response;
                    removeLoader();
                    showToast(
                        "File uploaded successfully, please continue to next step for mapping your columns.",
                        TOAST_TYPE.SUCCESS
                    );

                    let filteredMapping = columnMapping.mapping.filter((systemHeader) => {
                        return !EXCLUDE_FIELDS.includes(systemHeader.fieldName);
                    });

                    if (props.tableName === TABLES.EMPLOYEE && !PermissionUtils.hasAccess(MODULE.SALARY, ACTION.EDIT)) {
                        const EXCLUDE_SALARY_FIELDS = [];
                        filteredMapping = filteredMapping.filter((systemHeader) => {
                            return !EXCLUDE_SALARY_FIELDS.includes(systemHeader.fieldName);
                        });
                    }

                    const country = TenantService.get(TENANT.COUNTRY_CODE);                    
                    if (props.tableName === TABLES.PEOPLE_COMPONENT) {
                        filteredMapping = ComponentImportManager.FilterComponents(filteredMapping, props.components, country);
                    }

                    const mapping = filteredMapping.map((systemHeader) => {
                        return enrichMapping(systemHeader, country, customFieldResponse.customFields, getCustomFieldModule(props?.tableName));
                    });

                    props.setData({
                        filePath: filePath,
                        auditLogId: columnMapping.auditLogId,
                        mapping: mapping
                    });
                    uploadInput.current.value = "";
                }).catch((error) => {
                    removeLoader();
                    showToast(
                        "Something went wrong while uploading file to server",
                        TOAST_TYPE.FAILURE
                    );
                    uploadInput.current.value = "";
                    if(error?.status === 500 && error?.message?.includes("CONTENT_TYPE_NOT_ALLOWED")) {
                        showAlert(i18next.t('FAIL_TO_UPLOAD'), i18next.t('FILE_FORMAT_TYPE_ERROR_MSG'));
                    }
                });
        } else {
            showToast("Only CSV/XLSX file types are allowed", TOAST_TYPE.FAILURE);
        }
    };

    const onUploadInputClicked = (event) => {
        const selectedFiles = event.target.files;
        handleFiles(selectedFiles);
    };

    const onFileDrop = (event) => {
        event.stopPropagation();
        event.preventDefault();
        cloudImageRef.current.src = UploadCloud;
        const files = event.dataTransfer.files;
        handleFiles(files);
    };

    const onDragenter = (e) => {
        e.stopPropagation();
        e.preventDefault();
        cloudImageRef.current.src = UploadCloudFilled;
    };

    const onDragOver = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    function getImportPortal() {
        return (
            /* upload area */
            <div
                className="column parent-width bg-gray1 border-m border-radius-s align-items-center p-v-r p-h-l m-v-xl cursor-hand"
                onDragEnter={onDragenter}
                onDragOver={onDragOver}
                onDrop={onFileDrop}
                onClick={() => {
                    uploadInput.current.click();
                }}
            >
                <img
                    style={{ height: "100px", width: "200px" }}
                    className="m-r"
                    src={UploadCloud}
                    ref={cloudImageRef}
                    alt="Upload Cloud"
                />

                <div className="row parent-width justify-content-center position-relative p-h-xxl">
                    <DKLabel text="Drop .xlsx or .csv" />
                    <div className="row bg-button width-auto ml-r p-h-m fw-m text-white border-radius-m">
                        <DKIcon src={DKIcons.white.ic_document} className="ic-s" />
                        <DKLabel text="Choose a file" className="ml-r m-v-s" />
                    </div>
                    <input
                        className="parent-width parent-height position-absolute"
                        style={{ visibility: "hidden" }}
                        type="file"
                        accept=".csv,.xlsx,text/csv"
                        onChange={onUploadInputClicked}
                        ref={uploadInput}
                    />
                </div>

                <DKButton
                    icon={DKIcons.ic_download}
                    title="Download Sample File"
                    className=" border-m mt-xl fw-m mb-l"
                    onClick={(e) => {
                        e.stopPropagation();
                        const importType = getImportType(props?.tableName);
                        onDownload(importType);
                    }}
                />
            </div>
        );
    }

    function getImportType(tableName) {
        let type = "";
        switch(tableName) {
            case TABLES.ATTENDANCE:
                type = CONSTANTS.ATTENDANCE;
                break;
            case TABLES.EMPLOYEE:
                type = CONSTANTS.EMPLOYEE;
                break;
            case TABLES.CANDIDATE:
                type = CONSTANTS.CANDIDATE;
                break;
            case TABLES.PEOPLE_COMPONENT:
                type = CONSTANTS.COMPONENT;
                break;
            default :
                type = CONSTANTS.EMPLOYEE;
        }
        return type;
    }

    function getCustomFieldModule(tableName) {
        let module = "";
        switch(tableName) {
            case TABLES.EMPLOYEE:
                module = "employee_details";
                break;
            default :
                module = "employee_details";
        }

        return module;
    }

    function getFileInfoPanel() {
        return props.fileInfo ? (
            <div className="column parent-width mb-r">
                <DKLabel text="File details" className="fw-m" />
                <div className="row justify-content-between bg-gray1 border-m p-m border-radius-s mt-r">
                    <DKLabel text={props.fileInfo.name} className="" />
                    <DKLabel
                        className="text-blue"
                        text={`${Number(props.fileInfo.size) / 1000} Kb`}
                    />
                </div>
            </div>
        ) : null;
    }

    return (
        <div
            className="column align-items-center bg-white border-radius-s shadow-s p-xxl"
            style={{ width: "550px", paddingLeft: 60, paddingRight: 60 }}
        >
            <DKLabel text="File Uploader" className="fw-m fs-m" />
            <DKLabel
                text="Upload a file you want to import"
                className="text-gray mt-s text-align-center"
            />
            {getImportPortal()}
            {getFileInfoPanel()}
        </div>
    );
};

export default UploadFile;
